import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, Loader2, Sparkles, Award } from "lucide-react";
import { api } from "../lib/api";

/**
 * /our-journey — PUBLIC milestone timeline driven by the same
 * db.charity_settings document that powers the footer badge.
 *
 * The trustees never edit this file. They simply update Admin → Governance,
 * and the public timeline reflects their progress in real time. This is what
 * supporters and donors see when they want to follow our progress towards
 * CIO registration and beyond.
 */
const PHASES = [
  {
    key: "founded",
    title: "Network founded",
    sub: "Phase 0 · Origin",
    body:
      "A group of Ambedkarite Buddhist friends across the UK come together to build a charitable, public-benefit Sangha — rooted in the Dhamma and inspired by Babasaheb Dr B. R. Ambedkar.",
    isDone: () => true,
  },
  {
    key: "platform",
    title: "Charitable digital platform built",
    sub: "Phase 1 · Infrastructure",
    body:
      "Our own digital platform launches — meditation sessions, Dhamma talks, structured courses, study circles, youth learning and community meetings, all hosted as charitable mission infrastructure (not as a commercial product).",
    isDone: () => true,
  },
  {
    key: "governance",
    title: "Constitution & governance pack drafted",
    sub: "Phase 2 · Charity readiness",
    body:
      "Association CIO constitution (18 clauses), public-benefit statement, safeguarding, EDI, conflict-of-interest, code-of-conduct, risk register, complaints procedure, GDPR notice and trustee declarations — all published openly on this website.",
    isDone: () => true,
  },
  {
    key: "submitted",
    title: "CIO application submitted",
    sub: "Phase 3 · Submission",
    body:
      "Application lodged with the Charity Commission for England and Wales. The Network adopts the Association CIO model under the Charities Act 2011.",
    isDone: (s) => ["submitted", "under_review", "registered"].includes(s.status),
  },
  {
    key: "under_review",
    title: "Caseworker assigned",
    sub: "Phase 4 · Review",
    body:
      "A caseworker at the Charity Commission begins their review. We respond to questions promptly and transparently.",
    isDone: (s) => ["under_review", "registered"].includes(s.status),
  },
  {
    key: "registered",
    title: "Registered charity ✨",
    sub: "Phase 5 · Recognition",
    body:
      "The Charity Commission grants registration. Our charity number is published on every page of this website. We become accountable to a regulator beyond ourselves — and that is a good thing.",
    isDone: (s) => s.is_registered,
    extra: (s) =>
      s.is_registered && s.charity_number ? (
        <p className="mt-3 text-sm font-medium text-green-700">
          Charity number: <strong>{s.charity_number}</strong>
          {s.registered_at ? ` · registered ${new Date(s.registered_at).toLocaleDateString()}` : ""}
        </p>
      ) : null,
  },
  {
    key: "gift_aid",
    title: "Gift Aid live",
    sub: "Phase 6 · Multiplier",
    body:
      "HMRC approves our Gift Aid scheme. Every £1 a UK taxpayer donates becomes £1.25 for the charity, at no extra cost to the donor.",
    isDone: (s) => !!s.gift_aid_enabled,
  },
];

export default function OurJourney() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/charity/status")
      .then((r) => setS(r.data))
      .catch(() => setS({ status: "in_preparation", is_registered: false, gift_aid_enabled: false }))
      .finally(() => setLoading(false));
  }, []);

  const completedCount = s ? PHASES.filter((p) => p.isDone(s)).length : 0;
  const pct = Math.round((completedCount / PHASES.length) * 100);

  return (
    <div className="bg-pearl min-h-screen">
      <section className="bg-navy text-pearl py-20" data-testid="journey-hero">
        <div className="section-container">
          <p className="eyebrow text-gold">Transparency · Our charitable journey</p>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl">
            Becoming a charity, <span className="text-saffron italic">in public.</span>
          </h1>
          <p className="mt-6 text-pearl/80 max-w-2xl leading-relaxed">
            Charity status is not a finish line — it is a public commitment to be
            accountable. This page tracks our journey, updated live, so that
            supporters, donors, members and beneficiaries can see exactly
            where we are.
          </p>
          {!loading && (
            <div className="mt-8 max-w-md" data-testid="journey-progress">
              <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] text-pearl/60">
                <span>Progress</span>
                <span className="text-saffron">
                  {completedCount} of {PHASES.length} milestones complete
                </span>
              </div>
              <div className="mt-3 h-1.5 bg-pearl/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-saffron to-gold transition-all duration-700"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="py-16">
        <div className="section-container max-w-4xl">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-500">
              <Loader2 size={16} className="animate-spin" /> Loading timeline…
            </div>
          ) : (
            <ol className="relative border-l-2 border-saffron/30 ml-3" data-testid="journey-timeline">
              {PHASES.map((phase, idx) => {
                const done = phase.isDone(s);
                const isCurrent = !done && PHASES.slice(0, idx).every((p) => p.isDone(s));
                return (
                  <li key={phase.key} className="ml-8 mb-12" data-testid={`journey-phase-${phase.key}`}>
                    <span
                      className={`absolute -left-[13px] mt-1 inline-flex items-center justify-center w-6 h-6 rounded-full ring-4 ring-pearl ${
                        done
                          ? "bg-saffron text-navy"
                          : isCurrent
                          ? "bg-pearl text-saffron border-2 border-saffron animate-pulse"
                          : "bg-pearl text-slate-400 border-2 border-slate-300"
                      }`}
                    >
                      {done ? <CheckCircle2 size={14} /> : isCurrent ? <Sparkles size={12} /> : <Circle size={12} />}
                    </span>
                    <div className={`p-6 bg-white border rounded-sm ${
                      done ? "border-saffron/30" : isCurrent ? "border-saffron/60 shadow-lg" : "border-navy/10"
                    }`}>
                      <p className="text-[10px] uppercase tracking-[0.28em] text-saffron font-semibold">
                        {phase.sub}
                      </p>
                      <h2 className="font-display text-2xl text-navy mt-1.5">
                        {phase.title}
                      </h2>
                      <p className="mt-3 text-sm text-slate-700 leading-relaxed">
                        {phase.body}
                      </p>
                      {phase.extra && phase.extra(s)}
                      {isCurrent && (
                        <p className="mt-3 text-xs uppercase tracking-[0.22em] text-saffron font-semibold flex items-center gap-1.5">
                          <Sparkles size={12} /> We are here
                        </p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </section>

      <section className="py-16 bg-white border-t border-navy/5">
        <div className="section-container max-w-3xl text-center">
          <Award size={32} className="text-saffron mx-auto" />
          <h2 className="mt-4 font-display text-3xl text-navy">Walk this journey with us.</h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Becoming a registered charity is a meaningful step, and we want to do it
            with our community beside us. Join the Sangha, attend a session, donate
            (when you're able), or simply spread the word.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Link to="/membership" className="px-5 py-2.5 bg-navy text-pearl rounded-sm text-sm hover:bg-navy-light">
              Become a member
            </Link>
            <Link to="/donate" className="px-5 py-2.5 bg-saffron text-navy rounded-sm text-sm hover:bg-saffron-light">
              Support our work
            </Link>
            <Link to="/transparency" className="px-5 py-2.5 border border-navy/20 text-navy rounded-sm text-sm hover:bg-navy/5">
              Read our governance
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
